// Objectionable-content filter for reader-submitted text (comments and
// headline suggestions).
//
// App Store Review Guideline 1.2 requires apps with user-generated content to
// have "a method for filtering objectionable material from being posted".
// This is that method: a client-side blocklist that refuses the post outright,
// before anything reaches Supabase. Reporting (the second required mechanism)
// and per-author blocking (the third) live in useComments.
//
// Matching notes, because the naive version leaked:
//   - each blocked term becomes a regex where every letter may be masked by a
//     symbol (f*ck, sh#t) or padded (fuuuck, f.u.c.k). The FIRST letter must
//     be real, so a row of asterisks is not itself a slur.
//   - both ends require a non-letter, so "Scunthorpe" and "raccoon" pass.
//     Inflections that matter are listed explicitly instead of allowing a
//     free suffix, which would have flagged "spice" for "spic".
//   - no lookbehind: the iOS 15 deployment target predates Safari 16.4.

const BLOCKED_WORDS = [
  // racial / ethnic / religious slurs
  "nigger", "niggers", "nigga", "niggas", "chink", "chinks", "gook", "kike",
  "kikes", "spic", "spics", "wetback", "wetbacks", "raghead", "ragheads",
  "towelhead", "towelheads", "paki", "pakis", "coon", "coons", "beaner",
  "beaners", "jigaboo", "gyppo", "gypo",
  // homophobic / transphobic slurs
  "faggot", "faggots", "fag", "fags", "dyke", "dykes", "tranny", "trannies",
  "shemale", "shemales",
  // ableist slurs
  "retard", "retards", "retarded", "mongoloid",
  // sexual content
  "porn", "porno", "pornhub", "pornography", "blowjob", "blowjobs", "handjob",
  "cumshot", "creampie", "dildo", "dildos", "gangbang", "bukkake", "milf",
  "nudes", "camgirl", "jerk off", "jack off",
  // gendered abuse
  "whore", "whores", "slut", "sluts", "cunt", "cunts", "bitch", "bitches",
  "bitching",
  // general profanity
  "fuck", "fucks", "fucked", "fucking", "fucker", "fuckers", "motherfucker",
  "motherfuckers", "fuckwit", "shit", "shits", "shitty", "bullshit", "shite",
  "asshole", "assholes", "arsehole", "bastard", "bastards", "wanker",
  "wankers", "twat", "twats", "prick", "pricks", "dickhead", "dickheads",
  "piss off", "pissed off", "bollocks",
];

// Explicit threats and self-harm encouragement. Kept narrow on purpose: this
// is a site about quoting other people's language, so only unambiguous
// first-person threats and abuse are listed.
const BLOCKED_PHRASES = [
  "kill yourself", "kys", "go kill yourself", "go die", "hang yourself",
  "i will kill you", "i'll kill you", "im going to kill you",
  "i am going to kill you", "i will find you and kill",
  "should be shot", "should be hanged", "should be gassed",
  "deserve to die", "deserves to die", "rape you", "raping you",
];

// Symbols people use to mask a letter: f*ck, sh#t, b@stard.
const MASK = "*@#%&+$!?~^";
// Digit/symbol stand-ins for specific letters.
const LEET = {
  a: "4@", b: "8", e: "3", g: "9", i: "1!|", l: "1|", o: "0", s: "5$", t: "7", z: "2",
};

function escapeClass(chars) {
  return chars.replace(/[\\\]^-]/g, "\\$&");
}

// Build the regex for one term. Letters become character classes; the gaps
// between them allow up to two separators ("f.u.c.k", "f u c k").
function termToRegex(term) {
  const parts = [];
  const chars = term.toLowerCase().split("");
  let first = true;

  for (const ch of chars) {
    if (ch === " " || ch === "'") {
      // word gap inside a phrase, or an apostrophe that may be typed or not
      parts.push(ch === " " ? "[^a-z0-9]{1,3}" : "[^a-z0-9]{0,2}");
      continue;
    }
    if (!/[a-z]/.test(ch)) continue;

    const variants = ch + (LEET[ch] || "") + (first ? "" : MASK);
    parts.push(`[${escapeClass(variants)}]+`);
    if (first) first = false;
    parts.push("[^a-z0-9]{0,2}");
  }

  // drop the trailing separator so the closing boundary does the work
  if (parts[parts.length - 1] === "[^a-z0-9]{0,2}") parts.pop();

  return new RegExp(`(^|[^a-z0-9])${parts.join("")}([^a-z0-9]|$)`, "i");
}

// Compiled once at module load.
const WORD_PATTERNS = BLOCKED_WORDS.map(termToRegex);
const PHRASE_PATTERNS = BLOCKED_PHRASES.map(termToRegex);

// Fold accents so "fück" is caught, and normalise whitespace.
function fold(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[àáâãäå]/g, "a")
    .replace(/[èéêë]/g, "e")
    .replace(/[ìíîï]/g, "i")
    .replace(/[òóôõö]/g, "o")
    .replace(/[ùúûü]/g, "u")
    .replace(/[ç]/g, "c")
    .replace(/[ñ]/g, "n")
    .replace(/\s+/g, " ");
}

const SLUR_MESSAGE =
  "This can't be posted: slurs, sexual content and abusive language aren't allowed here. See our Terms of Use.";
const THREAT_MESSAGE =
  "This can't be posted: threats and abuse aren't allowed here. See our Terms of Use.";

/**
 * Decide whether a reader-submitted string may be posted.
 * @returns {{ok: boolean, reason: string}}
 */
export function checkText(text) {
  const raw = String(text || "").trim();

  if (!raw) return { ok: false, reason: "Write something first." };
  if (raw.length < 2) {
    return { ok: false, reason: "That's a bit short — say a little more." };
  }
  if (raw.length > 1500) {
    return { ok: false, reason: "That's over the 1,500-character limit." };
  }

  const folded = fold(raw);

  for (const re of PHRASE_PATTERNS) {
    if (re.test(folded)) return { ok: false, reason: THREAT_MESSAGE };
  }
  for (const re of WORD_PATTERNS) {
    if (re.test(folded)) return { ok: false, reason: SLUR_MESSAGE };
  }

  // Crude spam guards: a wall of shouting, or one character held down.
  const letters = raw.replace(/[^a-zA-Z]/g, "");
  if (letters.length > 20 && letters === letters.toUpperCase()) {
    return { ok: false, reason: "Please don't post in all caps." };
  }
  if (/(.)\1{9,}/.test(raw)) {
    return { ok: false, reason: "That looks like spam." };
  }

  return { ok: true, reason: "" };
}

export default checkText;
